import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { SPECIAL_EVENTS_ALLOCATION, SPECIAL_EVENTS_RATE_SHORT, SPECIAL_EVENTS_RATE_FULL, getContractYearStart, getContractYearLabel } from '../../lib/data';
import { format } from 'date-fns';

// Parse a plain 'YYYY-MM-DD' date as local, not UTC — new Date(str) reads
// it as UTC midnight, which date-fns then formats back in local time,
// occasionally showing the day before the one actually logged.
function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function emptyForm() {
  return {
    event_name: '', location: '', event_date: format(new Date(), 'yyyy-MM-dd'),
    worker_id: '', worker_name: '', hours_worked: '', event_type: 'short', notes: '',
  };
}

export default function SpecialEventsTracker() {
  const [events, setEvents] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetchEvents();
    supabase.from('profiles').select('id, full_name').in('role', ['worker', 'supervisor']).eq('is_test', false).order('full_name')
      .then(({ data }) => setWorkers(data || []));
  }, []);

  async function fetchEvents() {
    // Contract year (Apr 1 – Mar 31), computed from today so the 600hr
    // allocation gauge actually resets each year instead of accumulating
    // every event ever logged against a single fixed date forever.
    const yearStartStr = format(getContractYearStart(), 'yyyy-MM-dd');
    const { data, error } = await supabase
      .from('special_event_logs')
      .select('*')
      .gte('event_date', yearStartStr)
      .order('event_date', { ascending: false });
    if (error) {
      setLoadError(error.message);
      setLoading(false);
      return;
    }
    setLoadError(null);
    setEvents(data || []);
    setLoading(false);
  }

  async function saveEvent() {
    const hours = parseFloat(form.hours_worked);
    if (!form.event_name.trim() || !form.event_date || !Number.isFinite(hours) || hours <= 0) {
      setSaveError('Event name, date, and a positive number of hours are required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    // `is_overage` exists in the schema (true once cumulative contract-year
    // hours pass the 600hr allocation) but was never actually written on
    // insert, so it sat at its `false` default for every row. `events`
    // here already holds this contract year's logged hours, so we can
    // compute it the same way the on-screen gauge does.
    const isOverage = (totalHours + hours) > SPECIAL_EVENTS_ALLOCATION;
    const { error } = await supabase.from('special_event_logs').insert([{
      ...form,
      worker_id: form.worker_id || null,
      hours_worked: hours,
      is_overage: isOverage,
    }]);
    if (error) {
      setSaving(false);
      setSaveError(error.message);
      return;
    }
    setForm(emptyForm());
    setShowForm(false);
    setSaving(false);
    fetchEvents();
  }

  const totalHours = events.reduce((sum, e) => sum + (e.hours_worked || 0), 0);
  const pct = Math.min((totalHours / SPECIAL_EVENTS_ALLOCATION) * 100, 100);
  const remaining = Math.max(SPECIAL_EVENTS_ALLOCATION - totalHours, 0);
  const overageHours = Math.max(totalHours - SPECIAL_EVENTS_ALLOCATION, 0);

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>Special Events Tracker</div>
        <button style={styles.addBtn} onClick={() => { setForm(emptyForm()); setSaveError(null); setShowForm(true); }}>+ Log Event</button>
      </div>

      {loadError && (
        <div style={{ ...styles.overageBanner, marginBottom: 16, marginTop: 0 }}>
          Couldn't load event log: {loadError}
        </div>
      )}

      {/* Hours gauge. Values show "—" until the event log has loaded — a
          gauge reading 0 used / 600 remaining mid-load looks like real
          headroom and could lead to booking events that aren't available. */}
      <div style={styles.gaugeCard}>
        <div style={styles.gaugeRow}>
          <div>
            <div style={styles.gaugeValue}>{loading ? '—' : totalHours.toFixed(1)}</div>
            <div style={styles.gaugeLabel}>Hours Used</div>
          </div>
          <div style={styles.gaugeDivider} />
          <div>
            <div style={{ ...styles.gaugeValue, color: loading ? '#5B6B72' : (remaining < 100 ? '#B33A2E' : '#2F7A4F') }}>
              {loading ? '—' : remaining.toFixed(1)}
            </div>
            <div style={styles.gaugeLabel}>Hours Remaining</div>
          </div>
          <div style={styles.gaugeDivider} />
          <div>
            <div style={styles.gaugeValue}>{SPECIAL_EVENTS_ALLOCATION}</div>
            <div style={styles.gaugeLabel}>Contract-Year Allocation</div>
          </div>
        </div>

        <div style={styles.barTrack}>
          <div style={{
            ...styles.barFill,
            width: `${pct}%`,
            background: pct > 80 ? '#B33A2E' : pct > 60 ? '#B5730A' : '#2F7A4F'
          }} />
        </div>
        <div style={styles.barLabels}>
          <span style={styles.barLabel}>{loading ? 'Loading event log…' : `${pct.toFixed(1)}% used`}</span>
          <span style={styles.barLabel}>{loading ? '' : `${events.length} events logged`}</span>
        </div>

        {!loading && overageHours > 0 && (
          <div style={styles.overageBanner}>
            🚨 Over allocation by {overageHours.toFixed(1)} hours. These hours must be pre-approved and
            invoiced at overage rates (${SPECIAL_EVENTS_RATE_SHORT}/event short, ${SPECIAL_EVENTS_RATE_FULL}/event full-day).
          </div>
        )}
        {!loading && remaining < 60 && remaining > 0 && (
          <div style={styles.warningBanner}>
            ⚠️ Only {remaining.toFixed(0)} hours remaining before overage rates apply.
            Get pre-approval from Jason Lawrence before booking additional events.
          </div>
        )}
      </div>

      {/* Log event form */}
      {showForm && (
        <div style={styles.formCard}>
          <div style={styles.formTitle}>Log Special Event</div>
          <div style={styles.formGrid}>
            <Field label="Event Name" value={form.event_name}
              onChange={v => setForm(f => ({ ...f, event_name: v }))} placeholder="e.g. Staff Christmas Party" />
            <Field label="Location / Building" value={form.location}
              onChange={v => setForm(f => ({ ...f, location: v }))} placeholder="e.g. Paradise Double Ice Complex" />
            <Field label="Date" type="date" value={form.event_date}
              onChange={v => setForm(f => ({ ...f, event_date: v }))} />
            <div>
              <div style={styles.fieldLabel}>Worker</div>
              <select style={styles.select} value={form.worker_id}
                onChange={e => {
                  const id = e.target.value;
                  const w = workers.find(x => x.id === id);
                  setForm(f => ({ ...f, worker_id: id, worker_name: w?.full_name || '' }));
                }}>
                <option value="">— Select worker —</option>
                {workers.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
              </select>
            </div>
            <Field label="Hours Worked" type="number" value={form.hours_worked}
              onChange={v => setForm(f => ({ ...f, hours_worked: v }))} placeholder="e.g. 3.5" />
            <div>
              <div style={styles.fieldLabel}>Event Type</div>
              <select style={styles.select} value={form.event_type}
                onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
                <option value="short">Short (2–4 hours)</option>
                <option value="full">Full Day</option>
              </select>
            </div>
          </div>
          <Field label="Notes" value={form.notes}
            onChange={v => setForm(f => ({ ...f, notes: v }))} placeholder="Any relevant details..." />
          {saveError && (
            <div style={{ ...styles.overageBanner, marginTop: 4 }}>{saveError}</div>
          )}
          <div style={styles.formActions}>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={saving ? { ...styles.saveBtn, opacity: 0.6 } : styles.saveBtn} onClick={saveEvent} disabled={saving}>
              {saving ? 'Saving...' : 'Save Event'}
            </button>
          </div>
        </div>
      )}

      {/* Event log table */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Event Log — Contract Year {getContractYearLabel()}</div>
        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : events.length === 0 ? (
          <div style={styles.empty}>No special events logged yet. Use "Log Event" to add the first one.</div>
        ) : (
          <div style={styles.table}>
            <div style={styles.tableHeader}>
              <span>Date</span><span>Event</span><span>Location</span><span>Worker</span><span>Hours</span>
            </div>
            {events.map(e => (
              <div key={e.id} style={styles.tableRow}>
                <span style={styles.cell}>{format(parseLocalDate(e.event_date), 'MMM d, yyyy')}</span>
                <span style={styles.cell}>{e.event_name}</span>
                <span style={{ ...styles.cell, color: '#5B6B72' }}>{e.location}</span>
                <span style={{ ...styles.cell, color: '#5B6B72' }}>{e.worker_name}</span>
                <span style={{ ...styles.cell, color: '#1A2B33', fontWeight: 700 }}>{e.hours_worked}h</span>
              </div>
            ))}
            <div style={styles.tableFooter}>
              <span>TOTAL</span><span /><span /><span />
              <span style={{ color: '#1A2B33', fontWeight: 800 }}>{totalHours.toFixed(1)}h</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <div style={styles.fieldLabel}>{label}</div>
      <input style={styles.input} type={type} value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} />
    </div>
  );
}

const styles = {
  page: { padding: 20, background: '#F5F3EF', minHeight: 'calc(100vh - 130px)' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  pageTitle: { color: '#1A2B33', fontSize: 24, fontWeight: 800, letterSpacing: -0.5 },
  addBtn: { background: '#f97316', color: 'white', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  gaugeCard: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, padding: 20, marginBottom: 20 },
  gaugeRow: { display: 'flex', gap: 24, marginBottom: 16 },
  gaugeValue: { color: '#1A2B33', fontSize: 32, fontWeight: 800, letterSpacing: -1 },
  gaugeLabel: { color: '#5B6B72', fontSize: 12, marginTop: 2 },
  gaugeDivider: { width: 1, background: '#E4E0D8', alignSelf: 'stretch' },
  barTrack: { height: 10, background: '#E4E0D8', borderRadius: 5, overflow: 'hidden', marginBottom: 6 },
  barFill: { height: '100%', borderRadius: 5, transition: 'width 0.5s ease' },
  barLabels: { display: 'flex', justifyContent: 'space-between' },
  barLabel: { color: '#5B6B72', fontSize: 11 },
  overageBanner: { background: '#FBF1EF', border: '1px solid #B33A2E', color: '#B33A2E', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 12, lineHeight: 1.5 },
  warningBanner: { background: 'rgba(181,115,10,0.08)', border: '1px solid #B5730A', color: '#96600A', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 12, lineHeight: 1.5 },
  formCard: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, padding: 20, marginBottom: 20 },
  formTitle: { color: '#1A2B33', fontSize: 16, fontWeight: 700, marginBottom: 16 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 },
  fieldLabel: { color: '#5B6B72', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 },
  input: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 6, padding: '10px 12px', color: '#1A2B33', fontSize: 14, width: '100%', boxSizing: 'border-box' },
  select: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 6, padding: '10px 12px', color: '#1A2B33', fontSize: 14, width: '100%' },
  formActions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  cancelBtn: { background: 'transparent', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 6, padding: '10px 18px', cursor: 'pointer', fontSize: 14 },
  saveBtn: { background: '#f97316', color: 'white', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  section: { marginTop: 24 },
  sectionTitle: { color: '#5B6B72', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  empty: { color: '#5B6B72', fontSize: 14, padding: '20px 0' },
  table: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 10, overflow: 'hidden' },
  tableHeader: { display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 80px', padding: '10px 14px', background: '#F5F3EF', color: '#5B6B72', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, gap: 8 },
  tableRow: { display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 80px', padding: '10px 14px', borderTop: '1px solid #E4E0D8', gap: 8, alignItems: 'center' },
  tableFooter: { display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 80px', padding: '10px 14px', borderTop: '2px solid #E4E0D8', background: '#F5F3EF', gap: 8, color: '#5B6B72', fontWeight: 700, fontSize: 12 },
  cell: { color: '#1A2B33', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};
